import { useCallback, useEffect, useState } from "react";

import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";

import { Button, Box, Stack } from "@mui/material";
import { PageContainer } from "@/features/ui";

import { useAuth } from "@/features/authentication";
import { PermissionEnum } from "@/features/permissions";
import { apiClient, getErrorMessage } from "@/features/shared";
import { CenteredLoading, useSnackbar } from "@/features/ui";
import OutletAvailabilitySection from "@/features/users/user-form/outlet-availability-section";

export const UserOutlet = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const { showSnackbar } = useSnackbar();
  const [user, setUser] = useState(null);
  const [associatedOutlets, setAssociatedOutlets] = useState([]);
  const [loading, setLoading] = useState(false);

  const {
    control,
    handleSubmit,
    reset,
    formState: { isSubmitting },
  } = useForm();

  const loadingAssociatedOutlets = useCallback(async () => {
    setLoading(true);
    try {
      const [outletsResponse, userResponse] = await Promise.all([
        apiClient.get(`/users/${userId}/outlets`),
        apiClient.get(`/users/${userId}`),
      ]);
      setAssociatedOutlets(outletsResponse.data.data);
      setUser(userResponse.data.data);
    } catch (error) {
      showSnackbar(getErrorMessage(error, "Gagal memuat cabang toko"));
    } finally {
      setLoading(false);
    }
  }, [showSnackbar, userId]);

  useEffect(() => {
    loadingAssociatedOutlets();
  }, [loadingAssociatedOutlets]);

  useEffect(() => {
    reset({
      outlets: Object.fromEntries(
        associatedOutlets.map(({ id }) => [id, true])
      ),
    });
  }, [associatedOutlets, reset]);

  const onSubmit = async (data) => {
    try {
      await apiClient.post(`/users/${userId}/outlets`, {
        outletIds: Object.keys(data.outlets).filter((id) => data.outlets[id]),
      });
      navigate(-1);
    } catch (error) {
      showSnackbar(
        getErrorMessage(error, "Gagal memuat pengguna outlet")
      );
    }
  };

  return loading ? (
    <CenteredLoading />
  ) : (
    <PageContainer
      maxWidth="xl"
      title="Cabang Toko Pengguna"
      breadcrumbs={[
        { title: "Pengguna", path: "/setting/users" },
        ...(user
          ? [{ title: user.name, path: `/setting/users/${userId}` }]
          : []),
        { title: "Cabang Toko" },
      ]}
    >
      <Stack
        component="form"
        noValidate
        onSubmit={handleSubmit(onSubmit)}
        spacing={3}
      >
        <OutletAvailabilitySection
          control={control}
          disabled={!hasPermission(PermissionEnum.UPSERT_USER_OUTLET)}
        />
        {hasPermission(PermissionEnum.UPSERT_USER_OUTLET) && (
          <Box gap sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              type="submit"
              variant="contained"
              loading={isSubmitting}
            >
              Simpan
            </Button>
          </Box>
        )}
      </Stack>
    </PageContainer>
  );
};
